import { ReactNode, useEffect } from "react";

type AlertVariant = "success" | "error" | "warning" | "info";

export interface AlertProps {
  variant?: AlertVariant;
  title?: string;
  message: ReactNode;
  onClose?: () => void;
  autoCloseMs?: number;
}

// Enhanced with subtle gradients and stronger border accents
const variantStyles: Record<AlertVariant, string> = {
  success: "border-green-500/30 bg-white text-green-900 shadow-green-900/5",
  error: "border-red-500/30 bg-white text-red-900 shadow-red-900/5",
  warning: "border-yellow-500/30 bg-white text-yellow-900 shadow-yellow-900/5",
  info: "border-blue-500/30 bg-white text-blue-900 shadow-blue-900/5",
};

// Progress bar colors to match variants
const barStyles: Record<AlertVariant, string> = {
  success: "bg-green-500",
  error: "bg-red-500",
  warning: "bg-yellow-500",
  info: "bg-blue-500",
};

const iconMap: Record<AlertVariant, ReactNode> = {
  success: (
    <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  ),
  error: (
    <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  warning: (
    <svg className="h-5 w-5 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  ),
  info: (
    <svg className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
};

export function Alert({
  variant = "info",
  title,
  message,
  onClose,
  autoCloseMs = 5000,
}: AlertProps) {

  useEffect(() => {
    if (!onClose || autoCloseMs <= 0) return;

    const timer = setTimeout(() => {
      onClose();
    }, autoCloseMs);

    return () => clearTimeout(timer);
  }, [onClose, autoCloseMs]);

  return (
    <div
      className={`
        fixed top-4 right-4 z-[100]
        flex w-80 flex-col overflow-hidden rounded-xl border bg-white/95 
        backdrop-blur-md shadow-2xl animate-in slide-in-from-right-10 duration-300
        ${variantStyles[variant]}
      `}
    >
      <div className="flex items-start gap-3 p-4">
        <div className="flex-shrink-0">{iconMap[variant]}</div>

        <div className="flex-1 pt-0.5">
          {title && (
            <h4 className="font-semibold text-[13px] uppercase tracking-wider text-slate-500 mb-1">
              {title}
            </h4>
          )}
          <div className="text-sm font-medium leading-snug text-slate-800">
            {message}
          </div>
        </div>

        {onClose && (
          <button
            onClick={onClose}
            className="group -mr-1 rounded-lg p-1 transition-all hover:bg-slate-100"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-slate-400 group-hover:text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Visual Timer Progress Bar */}
      {autoCloseMs > 0 && (
        <div className="h-1 w-full bg-slate-100/50">
          <div 
            className={`h-full ${barStyles[variant]} transition-all linear`}
            style={{ 
              animation: `shrink ${autoCloseMs}ms linear forwards` 
            }}
          />
        </div>
      )}

      {/* Injected Keyframes for the bar and entrance */}
      <style>{`
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
}